import { inject, Injectable, InjectionToken, signal } from '@angular/core';
import { AppCheck, getToken } from '@angular/fire/app-check';
import { attestWithRetries, type AppCheckFailure } from '@parkingless/interface';

export type AppCheckStatus = 'pending' | 'verified' | 'failed';

/**
 * Where a token comes from, or `null` when this build registers no App Check provider
 * (the emulator configuration, and a real one whose site key is not set up yet — see
 * `environment.appCheck`). Injected so a test can hand in a fake.
 *
 * Not forcing a refresh: the SDK keeps the token fresh and persists it, so on every
 * load but the first the cached token answers at once and the gate is invisible.
 */
export const APP_CHECK_TOKEN_SOURCE = new InjectionToken<(() => Promise<string>) | null>(
  'APP_CHECK_TOKEN_SOURCE',
  {
    providedIn: 'root',
    factory: () => {
      const appCheck = inject(AppCheck, { optional: true });
      return appCheck ? () => getToken(appCheck).then((result) => result.token) : null;
    },
  },
);

/** The wait between attempts; a test replaces it so the schedule costs nothing. */
export const APP_CHECK_SLEEP = new InjectionToken<(ms: number) => Promise<void>>('APP_CHECK_SLEEP', {
  providedIn: 'root',
  factory: () => (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)),
});

/**
 * The App Check boot gate: verified, or blocked with a reason and a retry.
 *
 * The same gate the three mobile apps run. Once the functions enforce App Check
 * (`APP_CHECK_ENFORCE`, docs/SECURITY_AUDIT.md F12) a request without a token is
 * refused, so a console that rendered without one would only move the failure onto
 * whichever page made the first request. The loop itself (retries over ~15 s while
 * the network is down, none on a rejection) is in @parkingless/interface; `settled` is what the app
 * initializer in app.config.ts waits on, so the first navigation and its guards do not
 * read Firestore before a token is held. AppComponent renders the blocked page on
 * `failed` and offers `retry()`.
 */
@Injectable({ providedIn: 'root' })
export class AppCheckGateService {
  private readonly source = inject(APP_CHECK_TOKEN_SOURCE);
  private readonly sleep = inject(APP_CHECK_SLEEP);
  private running: Promise<void> | null = null;

  readonly status = signal<AppCheckStatus>(this.source ? 'pending' : 'verified');
  readonly failure = signal<AppCheckFailure | null>(null);

  /** Resolves when the first round has an answer, whichever it is. */
  readonly settled: Promise<void> = this.check();

  /** Runs the check again, from the blocked page. No-op while one runs or once verified. */
  retry(): Promise<void> {
    return this.check();
  }

  private check(): Promise<void> {
    if (!this.source || this.status() === 'verified') {
      return Promise.resolve();
    }
    if (this.running) {
      return this.running;
    }

    this.status.set('pending');
    this.running = attestWithRetries({ getToken: this.source, sleep: this.sleep })
      .then((outcome) => {
        if (outcome.status === 'verified') {
          this.failure.set(null);
          this.status.set('verified');
        } else if (outcome.status === 'failed') {
          console.error(`App Check: not verified after ${outcome.attempts} attempts (${outcome.failure})`, outcome.error);
          this.failure.set(outcome.failure);
          this.status.set('failed');
        }
      })
      .finally(() => {
        this.running = null;
      });
    return this.running;
  }
}
